import { Checkbox, List, ListItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { DiagramSeriesGroup, DiagramSeriesOption } from '../../types/DiagramSeries.types';
import { formatDiagramSeriesLabel } from '../../utils/formatDiagramSeriesLabel';
import { SeriesColorMarker } from '../series-color-marker/SeriesColorMarker';
import { SeriesGroupDivider, SeriesGroupItem, SeriesListItemButton } from './DiagramSeriesSelector.style';

type DiagramSeriesListItemProps = {
  series: DiagramSeriesOption;
  isSelected: boolean;
  color: string;
  onToggle: (seriesId: string) => void;
};

/** The whole row toggles the series; the checkbox only mirrors the state, so it stays out of the tab order. */
function DiagramSeriesListItem({ series, isSelected, color, onToggle }: DiagramSeriesListItemProps) {
  const label = formatDiagramSeriesLabel(series);

  return (
    <ListItem disablePadding>
      <SeriesListItemButton selected={isSelected} onClick={() => onToggle(series.id)}>
        <Checkbox
          checked={isSelected}
          color="secondary"
          size="medium"
          tabIndex={-1}
          inputProps={{ 'aria-label': label }}
          sx={{ p: 0 }}
        />
        <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
          {label}
        </Typography>
        {isSelected && <SeriesColorMarker aria-hidden seriesColor={color} valueType={series.valueType} />}
      </SeriesListItemButton>
    </ListItem>
  );
}

export type DiagramSeriesGroupListProps = {
  group: DiagramSeriesGroup;
  selectedIds: ReadonlySet<string>;
  getSeriesColor: (seriesId: string) => string;
  onToggle: (seriesId: string) => void;
};

/** One titled group of the selector: speed, measure channels or examined values. */
export function DiagramSeriesGroupList({ group, selectedIds, getSeriesColor, onToggle }: DiagramSeriesGroupListProps) {
  const { t } = useTranslation();

  return (
    <SeriesGroupItem>
      <SeriesGroupDivider>
        <Typography variant="overline" color="text.secondary">
          {t(`timeSeriesChart.channelSelector.groups.${group.id}`)}
        </Typography>
      </SeriesGroupDivider>
      <List disablePadding>
        {group.series.map((series) => (
          <DiagramSeriesListItem
            key={series.id}
            series={series}
            isSelected={selectedIds.has(series.id)}
            color={getSeriesColor(series.id)}
            onToggle={onToggle}
          />
        ))}
      </List>
    </SeriesGroupItem>
  );
}
